import { PositiveIcon, NegativeIcon } from "@datagame/ui";
import { motion, useAnimationControls } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import classNames from "classnames/bind";

import {
  CARD_ANIMATION_SEPARATION,
  CARD_TO_DECK_DURATION,
  CARD_TO_DECK_SEPARATION,
  CARD_TO_HAND_DURATION,
  CARD_TO_HAND_SEPARATION,
} from "../../../constants";
import { CardContent, DummyCardContent } from "../../shared/CardContent";
import { RankifierCardType } from "../../../contexts/rankifier";
import { RenderableDestinations } from "../../../contexts/rankifier/rankifierDestinations";
import { useRankifierCardPosition } from "../../../hooks/useRankifierCardPosition";
import { useRankifierState } from "../../../hooks/useRankifierState";
import { useGameVisuals } from "../../../contexts/gameVisuals";

import styles from "./RankifierCard.module.scss";

const cx = classNames.bind(styles);

interface CardProps {
  reverseImage: string;
  card: RankifierCardType;
  totalCards: number;
  index: number;
  skipInitialAnimation?: boolean;
}

export const RankifierCard = (props: CardProps) => {
  const { card, reverseImage, skipInitialAnimation = false } = props;
  const [
    {
      game: {
        configuration: {
          cardsPerHand,
          gameVisuals: { cardBackImage, showIcons, positiveColor, negativeColor },
        },
      },
      status: { isPickingFavorite, shouldChangeRound },
    },
    dispatch,
  ] = useRankifierState();
  const position = useRankifierCardPosition(card);
  const [isReversed, setIsReversed] = useState(true);
  const [isSocketedInDeck, setIsSocketedInDeck] = useState(false);
  const [isInMotion, setIsInMotion] = useState(false);
  const [isSocketedInSelected, setIsSocketedInSelected] = useState(false);
  const gameVisuals = useGameVisuals();

  const controls = useAnimationControls();

  const deckIndex = position.deck?.index ?? 0;
  const handIndex = position.hand?.index ?? 0;
  const cardIcon = isPickingFavorite ? (
    <PositiveIcon iconSetKey={gameVisuals?.iconSet} />
  ) : (
    <NegativeIcon iconSetKey={gameVisuals?.iconSet} />
  );

  useEffect(() => {
    if (!position.deck) {
      return;
    }

    if (skipInitialAnimation) {
      new Promise((res) => {
        setTimeout(() => res(controls.set("deck")), deckIndex - cardsPerHand < cardsPerHand ? cardsPerHand * 150 : 0);
      }).then(() => {
        setIsSocketedInDeck(true);
      });
    } else {
      controls.start("deck");
    }
  }, [position.deck, isSocketedInDeck]);

  useEffect(() => {
    position.hand && isSocketedInDeck && controls.start("hand");
  }, [position.hand, isSocketedInDeck]);

  useEffect(() => {
    position.selected && controls.start("selected");
  }, [position.selected]);

  const computedZIndex = useMemo(() => {
    if (isInMotion && position.selected) {
      return 3000 - position.selected.index;
    }

    if (position.selected) {
      return 4000 - position.selected.index;
    }

    if (isInMotion && position.hand) {
      return 5000 - deckIndex;
    }

    if (position.hand) {
      return 2000 - handIndex;
    }

    if (position.deck) {
      return 1000 - deckIndex;
    }

    return 0;
  }, [isInMotion, position]);

  const cardVariantGenerator = (status: RenderableDestinations) => ({
    bottom: "unset",
    top: 0,
    left: 0,
    x: position[status]?.x ?? 0,
    y: position[status]?.y ?? 0,
    width: position[status]?.width ?? 0,
    height: position[status]?.height ?? 0,
  });

  const cardWrapperVariants = {
    deck: {
      ...cardVariantGenerator("deck"),
      transition: {
        duration: CARD_TO_DECK_DURATION,
        delay: deckIndex * CARD_TO_DECK_SEPARATION,
        ease: "linear",
      },
    },
    hand: {
      ...cardVariantGenerator("hand"),
      transition: {
        duration: CARD_TO_HAND_DURATION,
        delay: handIndex * CARD_TO_HAND_SEPARATION + CARD_ANIMATION_SEPARATION,
        ease: "easeInOut",
      },
    },
    selected: {
      ...cardVariantGenerator("selected"),
      transition: {
        duration: CARD_TO_HAND_DURATION,
        delay: 0,
        ease: "easeInOut",
      },
    },
  };

  if (isSocketedInSelected) {
    return (
      <motion.div
        key={position.selected?.index}
        className={styles.rankifierCard}
        style={{
          zIndex: computedZIndex,
          ...cardVariantGenerator("selected"),
        }}
      >
        <DummyCardContent reverseImage={cardBackImage ? cardBackImage.url : reverseImage} />
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles.rankifierCard}
      animate={controls}
      initial={{
        top: "130%",
        left: "-30%",
      }}
      style={{
        zIndex: computedZIndex,
      }}
      exit={{
        opacity: 0,
        y: cardWrapperVariants.hand.y - 100,
        scale: 1.05,
        transition: {
          duration: 0.3,
        },
      }}
      onAnimationStart={(definition) => {
        setIsInMotion(true);

        if (definition === "selected") {
          setIsReversed(true);
        }
      }}
      onAnimationComplete={(definition: RenderableDestinations) => {
        setIsInMotion(false);

        switch (definition) {
          case "deck":
            setIsSocketedInDeck(true);
            break;
          case "hand":
            setIsReversed(false);
            break;
          case "selected":
            setIsSocketedInSelected(true);
            break;
        }
      }}
      variants={cardWrapperVariants}
    >
      <CardContent
        isReversed={isReversed}
        index={handIndex}
        reverseImage={cardBackImage ? cardBackImage.url : reverseImage}
        attribute={card.attribute}
        overlayClass={cx("overlay", { favorite: isPickingFavorite })}
        contentClass={styles.content}
        overlayBackground={`${isPickingFavorite ? positiveColor : negativeColor}85`}
        isDisabled={shouldChangeRound}
        icon={showIcons ? cardIcon : undefined}
        onClick={() => dispatch({ type: "SELECT_CARD", payload: { card } })}
      />
    </motion.div>
  );
};
